// frontend/src/components/ESIMOrders.js
import React, { useState, useEffect, useCallback } from "react";
import { Box, Alert } from "@mui/material";
import { useSnackbar } from "notistack";
import { esimAPI } from "../services/api";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import ESIMOrdersHeader from "./esim/ESIMOrdersHeader";
import ESIMOrdersTable from "./esim/ESIMOrdersTable";
import ESIMOrdersEmpty from "./esim/ESIMOrdersEmpty";
import ESIMOrdersLoading from "./esim/ESIMOrdersLoading";
import SMSDialog from "./esim/SMSDialog";
import ActionDialog from "./esim/ActionDialog";
import ESIMOrderDetails from "./esim/ESIMOrderDetails"; // Import the details dialog

const ESIMOrders = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [smsDialog, setSmsDialog] = useState(false);
  const [actionDialog, setActionDialog] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null); // Add state for selected order
  const [smsMessage, setSmsMessage] = useState("");

  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 10,
    totalCount: 0,
  });

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setPagination((prev) => ({
      ...prev,
      rowsPerPage: newRowsPerPage,
      page: 0,
    }));
  };

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Check if we have an order ID in the URL query parameters
      const params = new URLSearchParams(location.search);
      const orderId = params.get("id");

      const response = await esimAPI.getOrderDetails({
        page: pagination.page + 1, // backend uses 1-based pagination
        limit: pagination.rowsPerPage,
        id: orderId, // Add the order ID if present
      });

      if (response.data.success) {
        const fetchedOrders = response.data.orders || [];
        setOrders(fetchedOrders);
        setPagination((prev) => ({
          ...prev,
          totalCount: response.data.totalCount || fetchedOrders.length,
        }));

        // If order ID was specified, set it as selected
        if (orderId && fetchedOrders.length > 0) {
          const selectedOrder = fetchedOrders.find(
            (order) => order.id === orderId
          );
          if (selectedOrder) {
            setSelectedOrder(selectedOrder);
          }
        }
      } else {
        setOrders([]);
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        t("errors.fetchOrdersFailed");
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [
    pagination.page,
    pagination.rowsPerPage,
    location.search,
    enqueueSnackbar,
    t,
  ]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleAction = async (order, action) => {
    setActionDialog({
      order,
      action,
      title: t(`esim.orders.actions.${action}.title`),
      message: t(`esim.orders.actions.${action}.message`),
    });
  };

  // Add the view details handler
  const handleViewDetails = (order) => {
    setSelectedOrder(order);
  };

  const executeAction = async () => {
    const { order, action } = actionDialog;
    try {
      setLoading(true);

      let response;
      switch (action) {
        case "cancel":
          response = await esimAPI.cancel(order.orderNo);
          break;
        case "suspend":
          response = await esimAPI.suspend(order.iccid);
          break;
        case "unsuspend":
          response = await esimAPI.unsuspend(order.iccid);
          break;
        case "revoke":
          response = await esimAPI.revoke(order.iccid);
          break;
        default:
          throw new Error(t("errors.unknownAction", { action }));
      }

      if (response?.status === 200) {
        enqueueSnackbar(t(`esim.orders.notifications.${action}Success`), {
          variant: "success",
        });
        await fetchOrders();
      } else {
        throw new Error(response?.errorMsg || t(`errors.${action}Failed`));
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setLoading(false);
      setActionDialog(null);
    }
  };

  const handleSendSMS = async () => {
    if (!smsMessage.trim()) {
      enqueueSnackbar(t("esim.orders.smsDialog.enterMessage"), {
        variant: "warning",
      });
      return;
    }

    try {
      setLoading(true);
      await esimAPI.sendSMS(selectedOrder.iccid, smsMessage);
      enqueueSnackbar(t("esim.orders.smsDialog.success"), {
        variant: "success",
      });
      setSmsDialog(false);
      setSmsMessage("");
      setSelectedOrder(null);
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message || t("errors.smsFailed"), {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading && !orders.length) {
    return <ESIMOrdersLoading />;
  }

  if (!orders.length) {
    return <ESIMOrdersEmpty />;
  }

  return (
    <Box mt={4}>
      <ESIMOrdersHeader onRefresh={fetchOrders} loading={loading} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <ESIMOrdersTable
        orders={orders}
        loading={loading}
        onAction={handleAction}
        onSendSMS={(order) => {
          setSelectedOrder(order);
          setSmsDialog(true);
        }}
        onViewDetails={handleViewDetails} // Pass the handler
        pagination={{
          ...pagination,
          onPageChange: handleChangePage,
          onRowsPerPageChange: handleChangeRowsPerPage,
        }}
      />

      {/* Add the ESIMOrderDetails dialog */}
      <ESIMOrderDetails
        order={selectedOrder}
        open={!!selectedOrder && !smsDialog}
        onClose={() => setSelectedOrder(null)}
      />

      <SMSDialog
        open={smsDialog}
        onClose={() => {
          setSmsDialog(false);
          setSelectedOrder(null);
          setSmsMessage("");
        }}
        onSend={handleSendSMS}
        message={smsMessage}
        setMessage={setSmsMessage}
        loading={loading}
      />

      <ActionDialog
        dialog={actionDialog}
        onClose={() => setActionDialog(null)}
        onConfirm={executeAction}
        loading={loading}
      />
    </Box>
  );
};

export default ESIMOrders;
